/**
 * Project membership — who can reach one project inside an organisation.
 * Ported from `bimgo-ai-web`'s `projectMembersApi`.
 *
 * Three endpoints, and **three different body shapes**, which is the trap this
 * module exists to absorb:
 *   - `GET  /projects/{id}/members`             → `{ data: [...] }`
 *   - `POST /projects/{id}/members`             → `{ data: {...} }`  (an object)
 *   - `POST /projects/{id}/members/bulk-remove` → `{ data: {...} }`  (a summary)
 *
 * Two rules come from the backend rather than from taste, and both are encoded
 * here so the UI cannot forget them:
 *
 * 1. **Organisation admins are not removable.** Their access is implicit in the
 *    role, the row is read-only, and the API answers 409. Worse, bulk-remove is
 *    **all-or-nothing**: one org admin in the batch fails the *whole* request,
 *    taking the other removals with it. So they must never be offered for
 *    selection — `removableMembers` is the filter that guarantees it.
 * 2. **At most 200 ids per bulk-remove**, or the API answers 422. Splitting is
 *    the caller's job; `MAX_BULK_REMOVE` is here so nobody has to rediscover it.
 *
 * `not_found` in the result is **not** a failure: it means someone else removed
 * that member first, and the rest of the batch still succeeded.
 */
import { OrganizationsError, readDetail } from "./organizations";
import { bearer } from "./accessToken";
import { apiFetch } from "./apiFetch";

export interface ProjectMember {
  user_id: string;
  organization_id: string;
  added_at: string | null;
  added_by: string | null;
  /** Implicit access via the organization_admin role — read-only, 409 on change. */
  is_org_admin?: boolean;
}

export interface BulkRemoveResult {
  requested: number;
  removed: string[];
  /** Already gone. Not an error — the rest of the batch still applied. */
  not_found: string[];
}

/** The API's own limit; beyond it the request is refused with 422. */
export const MAX_BULK_REMOVE = 200;

/**
 * The members a UI may offer for removal.
 *
 * Org admins are excluded because including even one fails the entire batch —
 * so this is a correctness filter, not a cosmetic one.
 */
export function removableMembers(members: readonly ProjectMember[]): ProjectMember[] {
  return members.filter((m) => m.is_org_admin !== true);
}

async function request(
  method: "GET" | "POST",
  url: string,
  token: string | null,
  payload: unknown,
  fetchImpl: typeof fetch,
): Promise<unknown> {
  if (!token) throw new OrganizationsError({ kind: "unauthenticated" }, "not signed in");

  let response: Response;
  try {
    response = await fetchImpl(url, {
      method,
      headers: {
        Authorization: bearer(token),
        ...(payload === undefined ? {} : { "Content-Type": "application/json" }),
      },
      ...(payload === undefined ? {} : { body: JSON.stringify(payload) }),
    });
  } catch (error) {
    throw new OrganizationsError({ kind: "network", detail: String(error) }, String(error));
  }

  if (response.status === 401 || response.status === 403) {
    throw new OrganizationsError({ kind: "unauthenticated" }, "session rejected by the API");
  }

  const body: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const detail = readDetail(body);
    throw new OrganizationsError(
      { kind: "http", status: response.status, detail },
      detail ?? `HTTP ${response.status}`,
    );
  }
  return (body as { data?: unknown } | null)?.data;
}

function base(apiBaseUrl: string, projectId: string): string {
  return `${apiBaseUrl.replace(/\/+$/, "")}/projects/${encodeURIComponent(projectId)}/members`;
}

export async function getProjectMembers(
  token: string | null,
  projectId: string,
  deps: { apiBaseUrl: string; fetchImpl?: typeof fetch },
): Promise<ProjectMember[]> {
  const data = await request(
    "GET",
    base(deps.apiBaseUrl, projectId),
    token,
    undefined,
    deps.fetchImpl ?? apiFetch,
  );
  return Array.isArray(data) ? (data as ProjectMember[]) : [];
}

export async function addProjectMember(
  token: string | null,
  projectId: string,
  userId: string,
  deps: { apiBaseUrl: string; fetchImpl?: typeof fetch },
): Promise<ProjectMember | null> {
  const data = await request(
    "POST",
    base(deps.apiBaseUrl, projectId),
    token,
    { user_id: userId },
    deps.fetchImpl ?? apiFetch,
  );
  // An object here, not the array the list endpoint answers.
  return data && typeof data === "object" && !Array.isArray(data) ? (data as ProjectMember) : null;
}

export async function bulkRemoveProjectMembers(
  token: string | null,
  projectId: string,
  userIds: readonly string[],
  deps: { apiBaseUrl: string; fetchImpl?: typeof fetch },
): Promise<BulkRemoveResult> {
  if (userIds.length > MAX_BULK_REMOVE) {
    // Caught here rather than as a 422, so the caller gets a sentence instead of
    // a status code — and so the request is never sent.
    throw new OrganizationsError(
      { kind: "http", status: 422, detail: null },
      `at most ${MAX_BULK_REMOVE} members can be removed at once (asked for ${userIds.length})`,
    );
  }

  const data = await request(
    "POST",
    `${base(deps.apiBaseUrl, projectId)}/bulk-remove`,
    token,
    { user_ids: [...userIds] },
    deps.fetchImpl ?? apiFetch,
  );
  const result = (data ?? {}) as Partial<BulkRemoveResult>;
  return {
    requested: result.requested ?? userIds.length,
    removed: Array.isArray(result.removed) ? result.removed : [],
    not_found: Array.isArray(result.not_found) ? result.not_found : [],
  };
}
